import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";

interface CalendarProps {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  onChangeMonth?: (monthData: { month: number; year: number }) => void;
  enabledDateRanges?: string[] | null;
  className?: string;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onSelect,
  onChangeMonth,
  enabledDateRanges,
  className = "",
}) => {
  const [viewDate, setViewDate] = useState<Date>(new Date(selectedDate));

  useEffect(() => {
    setViewDate(new Date(selectedDate));
  }, [selectedDate]);

  const getFirstDayOfMonth = (date: Date): number => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const getDaysInMonth = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const handleNavigation = (direction: "next" | "prev") => {
    setViewDate((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + (direction === "next" ? 1 : -1));
      onChangeMonth?.({
        month: next.getMonth() + 1,
        year: next.getFullYear(),
      });
      return next;
    });
  };

  const headerText = viewDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const days: React.ReactElement[] = [];

  const firstDay = getFirstDayOfMonth(viewDate);
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="p-2" />);
  }

  const daysInMonth = getDaysInMonth(viewDate);
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const dateStr = moment(date).format("YYYY-MM-DD");
    const isSelected = dateStr === moment(selectedDate).format("YYYY-MM-DD");
    const hasData = enabledDateRanges && enabledDateRanges.includes(dateStr);
    const isDisabled = enabledDateRanges && !hasData;

    days.push(
      <div
        key={`day-${day}`}
        onClick={!isDisabled ? () => onSelect(date) : undefined}
        className={`p-2 relative text-sm rounded-2xl text-center cursor-pointer ${
          isSelected
            ? "bg-[var(--app-primary)] text-white"
            : isDisabled
            ? "text-gray-300 cursor-not-allowed"
            : "text-gray-700"
        }`}
      >
        {day}
        {hasData && !isSelected && (
          <div className="w-1.5 h-1.5 bg-[var(--app-primary)] rounded-full absolute bottom-0 left-1/2 -translate-x-1/2" />
        )}
      </div>
    );
  }

  return (
    <div className={`p-4 ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm font-medium text-gray-700">{headerText}</span>
        <div className="flex items-center gap-1">
          <button
            className="p-1 rounded-full hover:bg-gray-100"
            onClick={() => handleNavigation("prev")}
          >
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <button
            className="p-1 rounded-full hover:bg-gray-100"
            onClick={() => handleNavigation("next")}
          >
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            className="p-1 text-xs text-center text-gray-400 font-medium"
          >
            {d}
          </div>
        ))}
        {days}
      </div>
    </div>
  );
};

export default Calendar;
